import { Box, Collapse } from "@mui/material";
import { MenuBook } from "@mui/icons-material";
import { SectionHeader } from "./SectionHeader";
import { SectionDescription } from "./SectionDescription";
import { SectionNotes } from "./SectionNotes";
import { SectionFiles } from "./SectionFiles";
import { SectionFeedback } from "./SectionFeedback";
import { SectionChat } from "./SectionChat";
import { Features, UserRole } from "../../Enums/companyStatus";
import {
  AnswerDetail,
  CategoryDetail,
  SingleEvaluationQuestions,
} from "../../Models/evaluation";
import colors from "../../Constants/colors";
import { getGradeText } from "../../Helpers/gradeHelpers";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";

export interface EvaluationSectionCardProps {
  section: CategoryDetail;
  questions?: SingleEvaluationQuestions[];
  answers: AnswerDetail[];
  sectionKey: string;
  isExpanded: boolean;
  onToggleExpand: (sectionId: string) => void;
  onSaveFeedback: (
    sectionId: string,
    responses: Record<string, string>,
  ) => void;
}

export function EvaluationSectionCard({
  section,
  answers,
  isExpanded,
  sectionKey,
  questions,
  onToggleExpand,
  onSaveFeedback,
}: EvaluationSectionCardProps) {
  const categoryQuestions = questions?.find(
    (q) => q.question_category_key === sectionKey,
  )?.questions;

  return (
    <Box
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <SectionHeader
        grade={section.category_grade}
        title={section.name}
        gradeDescription={getGradeText(section.category_grade)}
        hasQuestions={(categoryQuestions || []).length > 0}
        isExpanded={isExpanded}
        onToggle={() => onToggleExpand(sectionKey)}
      />

      <Collapse in={isExpanded}>
        <Box
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: "white",
            borderTop: `1px solid ${colors.gray200}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {section.summary && (
              <SectionDescription description={section.summary} />
            )}

            {categoryQuestions && categoryQuestions.length > 0 && (
              <SectionFeedback
                sectionId={sectionKey}
                questions={categoryQuestions}
                answers={answers}
                onSaveFeedback={onSaveFeedback}
              />
            )}

            {section.note && <SectionNotes note={section.note} />}

            {section.company_data_sources &&
              section.company_data_sources.length > 0 && (
                <SectionFiles files={section.company_data_sources} />
              )}

            {section.question_category_sources &&
              section.question_category_sources.length > 0 && (
                <SectionFiles
                  files={section.question_category_sources}
                  icon={MenuBook}
                />
              )}
            {(hasPermission(Features.EXPERT_CONSULTATION) ||
              hasRole(UserRole.ADMIN)) && (
              <SectionChat
                section={section}
                questions={categoryQuestions}
                answers={answers}
              />
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
